// useNodeOpenIntent.js

import { useCallback } from 'react';

// Routing
import { useNavigate } from 'react-router-dom';

// Context
import { useBase } from 'context/BaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodePeek } from 'context/NodePeekContext';
import { useOptionalSketch } from 'context/SketchContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Services
import { adoptCatalogEntry } from 'services/catalog/CatalogAdoptService';

// Utilities
import { SectionType } from 'utilities/Enums';
import { shouldShowPeekOnOpen } from 'utilities/nodeOpenPolicy';
import { isSketchType } from 'utilities/sketchType';
import { buildNodePath, getSectionPath } from 'utilities/Routing';
import {
  getNodeCatalogId,
  isCatalogOptionValue,
  parseCatalogOptionValue,
} from 'utilities/PlatformCatalog';
import {
  commitProvisional,
  getProvisionalSession,
  isProvisional,
} from 'node/provisional/provisionalNodeSession';

/**
 * useNodeOpenIntent
 *
 * Opens Peek by default (or when the type's Show Peek on open is true).
 * Expands to /node/:id when preferPeek is false, or when the type opts out.
 * Catalog placeholders adopt into a workspace pointer before open.
 */
const useNodeOpenIntent = () => {

  // ----- ROUTING ----- //

  const navigate = useNavigate();

  // ----- CONTEXT ----- //

  const { activeBase } = useBase();
  const { setCurrentNode, setCurrentSection } = useNavigation();
  const { getType } = useNodeTypes();
  const { user, setDisplayedUser } = useUser();
  const { isPeekOpen, peekNodeKey, openPeek, closePeek } = useNodePeek();
  const sketch = useOptionalSketch();

  // ----- HANDLERS ----- //

  /**
   * Navigates to the full node section (URL-backed).
   *
   * @param {object} node
   */
  const openExpanded = useCallback(async (node) => {
    if (!node?.key) return;

    const session = getProvisionalSession(node.key);
    const shouldCommit = isProvisional(node)
      || Boolean(session && !session.committed);
    if (shouldCommit) {
      await commitProvisional(node.key);
    }

    const committedNode = shouldCommit
      ? { ...node, provisional: false }
      : node;

    const hasLoadedShape = Boolean(
      (typeof committedNode?.type === 'string' && committedNode.type.trim())
      || (typeof committedNode?.title === 'string' && committedNode.title.trim())
    );

    if (hasLoadedShape) {
      setCurrentNode(committedNode);
    }

    if (committedNode.type === 'USER') {
      setDisplayedUser(committedNode);
      setCurrentSection(SectionType.USER);
      navigate(getSectionPath(SectionType.USER, { profileKey: committedNode.key }));
      return;
    }

    setCurrentSection(SectionType.NODE);
    navigate(buildNodePath(committedNode.key));
  }, [navigate, setCurrentNode, setCurrentSection, setDisplayedUser]);

  /**
   * If the row is a not-yet-adopted catalog placeholder, create the pointer node.
   *
   * @param {object} node
   * @returns {Promise<object|null>}
   */
  const resolveOpenableNode = useCallback(async (node) => {
    if (!node?.key) return null;

    const isPlaceholder = node.isCatalogPlaceholder === true
      || isCatalogOptionValue(node.key);
    if (!isPlaceholder) return node;

    const catalogId = getNodeCatalogId(node) || parseCatalogOptionValue(node.key);
    const typeKey = String(node.type || '').trim();
    const baseKey = String(node.baseKey || activeBase?.key || '').trim();
    if (!catalogId || !typeKey || !baseKey || !user) {
      console.error('[useNodeOpenIntent] cannot adopt catalog placeholder', {
        catalogId,
        typeKey,
        baseKey,
      });
      return null;
    }

    try {
      return await adoptCatalogEntry({
        typeKey,
        catalogId,
        user,
        baseKey,
        typeDef: getType(typeKey),
      });
    } catch (error) {
      console.error('[useNodeOpenIntent] catalog adopt failed:', error);
      return null;
    }
  }, [activeBase?.key, getType, user]);

  /**
   * Opens a node in Peek, or expands to Workspace.
   *
   * @param {object} node
   * @param {object} [options]
   * @param {boolean} [options.preferPeek] - Force Peek (true) or Workspace (false).
   *   When omitted, uses the type's Show Peek on open setting (default true).
   */
  const openNode = useCallback(async (node, options = {}) => {
    if (!node?.key) return;

    const resolved = await resolveOpenableNode(node);
    if (!resolved?.key) return;

    if (resolved.type === 'USER') {
      closePeek();
      await openExpanded(resolved);
      return;
    }

    if (isSketchType(resolved.type) && typeof sketch?.openSketch === 'function') {
      sketch.openSketch(resolved);
      return;
    }

    const typeWantsPeek = shouldShowPeekOnOpen(getType(resolved.type));
    const shouldPeek = options.preferPeek === true
      ? true
      : options.preferPeek === false
        ? false
        : typeWantsPeek;

    if (shouldPeek) {
      const nextKey = typeof resolved?.key === 'string' ? resolved.key.trim() : '';
      if (!nextKey) return;

      const currentPeekKey = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
      if (isPeekOpen && currentPeekKey === nextKey) return;

      openPeek(resolved, {
        mode: isPeekOpen ? 'push' : 'reset',
      });
      return;
    }

    // Commit provisional before closePeek so abandon does not hard-delete.
    await openExpanded(resolved);
    closePeek();
  }, [
    closePeek,
    getType,
    isPeekOpen,
    openExpanded,
    openPeek,
    peekNodeKey,
    resolveOpenableNode,
    sketch,
  ]);

  return {
    openNode,
    openExpanded,
  };
};

export default useNodeOpenIntent;
